import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Modal, Table, Input, Select, DatePicker, Space, Tag, Form, Segmented, Checkbox } from 'antd';
import { SearchOutlined, CloseCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import TagFilter from 'components/TagFilter';
import DateRangePickerCombo from 'components/DateRangePickerCombo';
import { useDispatch, useSelector } from 'react-redux';
import { getAuditlogs } from 'services/misc.service';

import "./AuditlogModal.scss"

const PageSize = 10

// placeholder action type keys/values
const AuditLogFilterType = {
    All: 0,
    Customer: 1,
    Order: 2
};

export const Mode = {
    Customer: 1,
    Order: 2
}

export default function AuditLogModal({
    visible, onClose, customerId, orderId, userId, customerName, mode = Mode.Customer
}) {
    const [searchText, setSearchText] = useState('');
    const [debouncedKeyword, setDebouncedKeyword] = useState('');

    // date filters (both optional)
    const [dateFrom, setDateFrom] = useState(null); // moment | null
    const [dateTill, setDateTill] = useState(null); // moment | null

    // action type filter (defaults to all)
    const [auditLogFilterType, setAuditLogFilterType] = useState(AuditLogFilterType.All);

    // guests-only checkbox + selected staff (when not guests-only)
    const [guestsOnly, setGuestsOnly] = useState(false);
    const [selectedStaffId, setSelectedStaffId] = useState(null);

    const wholeStaff = useSelector(state => state.user?.wholeStaff ?? []); // expects [{ staffId, name }, ...]

    const [auditlogs, setAuditlogs] = useState({
        data: {},
        isLoading: true
    })
    const [pagination, setPagination] = useState({
        skip: 0,
        pageSize: PageSize,
        totalRecords: 0
    })

    const dispatch = useDispatch()
    const abortControllerRef = useRef(null)

    // debounce searchText -> debouncedKeyword and reset to first page
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedKeyword(searchText?.trim() ?? '');
            setPagination(prev => ({ ...prev, skip: 0 })); // go to first page on new search
        }, 500);

        return () => clearTimeout(handler);
    }, [searchText]);

    useEffect(() => {
        let isActive = true;

        // abort previous request
        if (abortControllerRef.current) {
            try { abortControllerRef.current.abort(); } catch (e) { /* ignore */ }
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;

        async function load() {
            try {
                setAuditlogs(data => ({
                    ...data, isLoading: true
                }));

                const initiatorId = guestsOnly ? userId : (selectedStaffId || undefined);

                const params = {
                    customerId,
                    orderId,
                    // isSystemGenerated: false,
                    keyword: debouncedKeyword || undefined,
                    skip: pagination.skip,
                    pageSize: pagination.pageSize,
                    dateFrom: dateFrom ? dateFrom.startOf('day').toISOString() : undefined,
                    dateTill: dateTill ? dateTill.endOf('day').toISOString() : undefined,
                    auditLogFilterType: auditLogFilterType !== AuditLogFilterType.All ? auditLogFilterType : undefined,
                    initiatorId
                };

                // pass signal as second arg (service signature expects it)
                const res = await dispatch(getAuditlogs({ ...params }, controller.signal));

                if (controller.signal.aborted || !isActive) return;

                const resData = res?.data ?? {};
                const items = resData?.items ?? [];
                const total = resData?.totalRecords ?? resData?.total ?? resData?.totalCount ?? 0;

                setAuditlogs({
                    data: { items },
                    isLoading: false
                });

                setPagination(prev => ({ ...prev, totalRecords: total }));
            } catch (err) {
                if (err?.name === 'AbortError') return;
                if (!isActive) return;
                setAuditlogs({
                    data: { items: [] },
                    isLoading: false
                })
            }
        }

        if (visible && (customerId || orderId) && userId) load();
        else {
            setAuditlogs({
                data: { items: [] },
                isLoading: false
            })
            handleClearFilters();
        }

        return () => {
            isActive = false;
            try { controller.abort(); } catch (e) { }
        }
    }, [customerId, orderId, userId, visible, debouncedKeyword, pagination.skip, pagination.pageSize, dateFrom, dateTill, auditLogFilterType, guestsOnly, selectedStaffId])
    // ...existing code...
    const users = useMemo(() => {
        const uniqueUsers = new Map();
        // ...existing code...
        return Array.from(uniqueUsers, ([id, name]) => ({
            value: id,
            label: name
        }));
    }, []);

    // Filter data based on all filters
    const filteredData = useMemo(() => {
        return auditlogs?.data?.items ?? []
    }, [auditlogs]);

    const handleClearFilters = () => {
        setSearchText('');
        setDateFrom(null);
        setDateTill(null);
        setGuestsOnly(false);
        setSelectedStaffId(null);
        setAuditLogFilterType(AuditLogFilterType.All);
        setPagination(prev => ({ ...prev, skip: 0 }));
    };



    const hasFilters = searchText || dateFrom || dateTill || guestsOnly || selectedStaffId || (auditLogFilterType !== AuditLogFilterType.All)

    const columns = [
        {
            title: 'Date/Time',
            key: 'datetime',
            width: 150,
            render: (item) => {
                const utc = moment.utc(item.changedAt);
                const cst = utc.tz("America/Chicago");

                return <span>{cst?.format("MMMM, DD YYYY h:mm a")}<br /> (CST)</span>
            }
        },
        {
            title: 'Changed By',
            key: 'changedBy',
            width: 120,
            render: (item) => {
                return <span>{item?.initiatorName}</span>
            }
        },
        {
            title: 'Order ID',
            key: 'orderId',
            width: 80,
            render: item => <span>{item?.orderId}</span>
        },
        {
            title: 'Property',
            key: 'property',
            width: 140,
            render: item => {

                return <>
                    {item?.propertyName || "-"}
                    </>
            }
        },
                    {
                        title: 'Old Value',
                    key: 'oldValue',
                    width: 140,
            render: item => <span>{item?.oldValue || "-"}</span>
        },
                    {
                        title: 'New Value',
                    key: 'newValue',
                    width: 140,
            render: item => <span>{item?.newValue || "-"}</span>
        },
                    {
                        title: 'Description',
                    key: 'description',
                    width: 360,
            render: item => <span>{item?.displayMessage}</span>
        },

                    ];

    const handleTableChange = (pager) => {
        const current = pager.current || 1;
                    const pageSize = pager.pageSize || pagination.pageSize;
        setPagination(prev => ({
                        ...prev,
                        skip: (current - 1) * pageSize,
                    pageSize
        }));
    };

                    return (
                    <Modal
                            title={`Audit logs ${customerId && mode === Mode.Customer ? `for customer ${customerName}` : ''} ${orderId ? `for order #${orderId}` : ''}`}
                        open={visible}
                        onCancel={onClose}
                        footer={null}
                        width={1500}
                        bodyStyle={{ padding: '24px' }}
                        destroyOnClose
                        className='antdModal'
                    >
                        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
                            <Input
                                placeholder="Search audit logs..."
                                prefix={<SearchOutlined />}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                style={{ width: 250 }}
                                allowClear
                            />
                            {/* Date range filters (both optional) */}
                            <DatePicker
                                placeholder="From"
                                value={dateFrom}
                                onChange={(d) => { setDateFrom(d); setPagination(prev => ({ ...prev, skip: 0 })); }}
                                allowClear
                                format="MMM DD, YYYY"
                            />
                            <DatePicker
                                placeholder="To"
                                value={dateTill}
                                onChange={(d) => { setDateTill(d); setPagination(prev => ({ ...prev, skip: 0 })); }}
                                allowClear
                                format="MMM DD, YYYY"
                                disabledDate={(current) => {
                                    if (!dateFrom) return false;
                                    // disable dates strictly before the selected "From" date (compare by day)
                                    return current && current.isBefore(dateFrom.startOf('day'), 'day');
                                }}
                            />
                            {
                                !orderId &&
                                <Select
                                    value={auditLogFilterType}
                                    onChange={(val) => { setAuditLogFilterType(val); setPagination(prev => ({ ...prev, skip: 0 })); }}
                                    style={{
                                        width: 160,
                                    }}
                                    className='borderedSelect'
                                    options={[
                                        { label: 'All', value: AuditLogFilterType.All },
                                        { label: 'Customer', value: AuditLogFilterType.Customer },
                                        { label: 'Order', value: AuditLogFilterType.Order }
                                    ]}
                                />
                            }

                            <Checkbox
                                className='auditCheckbox'
                                checked={guestsOnly}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setGuestsOnly(checked);
                                    // when switching to guests-only, clear selected staff; always reset to first page
                                    if (checked) setSelectedStaffId(null);
                                    setPagination(prev => ({ ...prev, skip: 0 }));
                                }}
                                disabled={!userId}
                            >
                                Guests only
                            </Checkbox>

                            {!guestsOnly && (
                                <Select
                                    placeholder="Select staff"
                                    value={selectedStaffId}
                                    onChange={(val) => { setSelectedStaffId(val); setPagination(prev => ({ ...prev, skip: 0 })); }}
                                    style={{ width: 220 }}
                                    allowClear
                                    className='borderedSelect'
                                    showSearch
                                    // use the option's label for searching
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={(wholeStaff || []).map(s => ({ label: s.name, value: s.userId }))}
                                />
                            )}

                        </div>

                        {hasFilters && (
                            <div
                                onClick={handleClearFilters}
                                className='auditClearFilter'
                                style={{ marginBottom: 16 }}
                            >
                                <CloseCircleOutlined />
                                Clear
                            </div>
                        )}

                        <Table
                            columns={columns}
                            dataSource={filteredData}
                            rowKey="id"
                            loading={auditlogs?.isLoading}
                            pagination={{
                                current: Math.floor(pagination.skip / pagination.pageSize) + 1,
                                pageSize: pagination.pageSize,
                                total: pagination.totalRecords,
                                showSizeChanger: true,
                                // showTotal: (total) => `Total ${total} logs`,
                            }}
                            onChange={handleTableChange}
                            scroll={{ x: 'max-content' }}
                        />

                    </Modal>
                    );
}
